import React, { useCallback, useMemo, useState } from 'react';
import { Button, H2, P, Stack } from '@fashionnovateam/nova-ui-toolkit';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import cn from 'classnames';
import Page from '../../Page';
import Header from '../../Header';
import {
  ItemFilters,
  useGetVendorItem,
  useGetVendorItems,
  VendorCatalogItem,
  possibleParams,
} from '../../../Lib/Hooks/ItemService';
import { useParamFilter } from '../../../Lib/Hooks/Filters/useParamFilter';
import { SidebarListItem } from './SidebarListItem';
import { HeaderContent, SidebarToggleButton } from './ItemDetailsLayoutHeader';

type ItemDetailsLayoutProps = {
  children: React.ReactNode;
};

/**
 * A layout component for displaying item details with navigation features such as viewing the next or previous item, and a sidebar for item navigation.
 *
 * @param {Object} props The properties to configure the ItemDetailsLayout component.
 * @param {React.ReactNode} props.children The child components to be rendered within the layout.
 */
function ItemDetailsLayout({ children }: ItemDetailsLayoutProps) {
  const { id: currentUserId, itemId: currentItemId } = useParams<{
    id: string;
    itemId: string;
  }>();
  const [sideBarOpen, setSideBarOpen] = useState<boolean>(true);
  const location = useLocation();
  const userId = useMemo(() => location.pathname.split('/')[2], [location]);
  const { paramFilters: urlParamFilters } = useParamFilter(possibleParams);

  // WARNING - Don't add a dependency to this useMemo, it will cause the component to re-render on every location change
  // The empty dependency array is intentional because `currentItemId` is not expected to change during the component's lifecycle.
  // This ensures that `targetId` remains stable and avoids unnecessary re-renders, which could degrade performance.
  const targetId = useMemo(() => currentItemId, []);

  const {
    isLoading,
    isError,
    data: targetItem,
  } = useGetVendorItem(currentUserId, targetId);

  const { data: currentItem } = useGetVendorItem(currentUserId, currentItemId);

  const {
    items: fetchedItems,
    isLoading: isItemListLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetVendorItems(
    currentUserId,
    { paramFilters: urlParamFilters },
    { staleTime: 30 * 1000 },
  );

  const reorderedItems: VendorCatalogItem[] | null = useMemo(() => {
    if (!fetchedItems.length) return null;
    if (!targetId || !targetItem) return fetchedItems;

    // Put the targeted id as the first element of the list
    const itemsWithoutTarget = fetchedItems.filter(
      (item) => item.id !== targetId,
    );
    return [targetItem, ...itemsWithoutTarget];
  }, [fetchedItems, targetItem, targetId]);

  const currentItemsIndex = useMemo(() => {
    if (!reorderedItems) return null;
    return reorderedItems.findIndex((item) => item.id === currentItemId);
  }, [currentItemId, reorderedItems]);

  const stage = useMemo(() => currentItem?.stage, [currentItem]);

  const stageToBadgeColor = useMemo(() => {
    switch (stage) {
      case ItemFilters.PENDING_VENDOR:
        return 'primary';
      case ItemFilters.REJECTED:
        return 'red';
      case ItemFilters.PO_DRAFTS:
      case ItemFilters.MANAGER_REVIEW:
      case ItemFilters.FINAL_REVIEW:
        return 'purple';
      case ItemFilters.PO_CREATED:
        return 'green';
      default:
        return 'neutral';
    }
  }, [stage]);

  const nextItemLink = useMemo(() => {
    if (
      !targetItem ||
      isError ||
      isLoading ||
      !reorderedItems ||
      currentItemsIndex === null ||
      currentItemsIndex < 0 ||
      currentItemsIndex === reorderedItems.length - 1
    ) {
      return '';
    }
    const nextItemId = reorderedItems[currentItemsIndex + 1].id;
    return `/users/${currentUserId}/sell-to-us/${nextItemId}${location.search}`;
  }, [
    targetItem,
    isError,
    isLoading,
    currentItemsIndex,
    reorderedItems,
    location.search,
    currentUserId,
  ]);

  const previousItemLink = useMemo(() => {
    if (
      !targetItem ||
      isError ||
      isLoading ||
      !reorderedItems ||
      currentItemsIndex === null ||
      currentItemsIndex < 1
    ) {
      return '';
    }
    const previousItemId = reorderedItems[currentItemsIndex - 1].id;
    return `/users/${currentUserId}/sell-to-us/${previousItemId}${location.search}`;
  }, [
    targetItem,
    isError,
    isLoading,
    currentItemsIndex,
    reorderedItems,
    location.search,
    currentUserId,
  ]);

  const toggleSidebar = useCallback(() => {
    setSideBarOpen((prevState) => !prevState);
  }, []);

  return (
    <Page.Container>
      <Header.Major
        title="Sell To Us"
        icon="wallet/wallet-settings"
        onCloseLink={`/users/${currentUserId}/sell-to-us${location.search}`}
        beforeTitleChildren={
          <SidebarToggleButton isOpen={sideBarOpen} onToggle={toggleSidebar} />
        }
      >
        <HeaderContent
          item={targetItem}
          isError={isError}
          isLoading={isLoading}
          stage={stage}
          badgeColor={stageToBadgeColor}
          nextItemLink={nextItemLink}
          previousItemLink={previousItemLink}
          reorderedItems={reorderedItems}
          currentItemsIndex={currentItemsIndex}
        />
      </Header.Major>
      {!targetItem || isError || isLoading ? (
        <div className="h-full flex items-center justify-center flex-col gap-2">
          {isLoading ? (
            <P>Loading...</P>
          ) : (
            <>
              <H2 className="text-2xl">Item not found.</H2>
              <Link
                data-testid="back-to-dashboard"
                to={`/users/${currentUserId}/sell-to-us`}
              >
                <Button>Back to dashboard</Button>
              </Link>
            </>
          )}
        </div>
      ) : (
        <Stack className="w-full h-full overflow-hidden" direction="row">
          <Stack
            data-testid="items-sidebar"
            gap="none"
            direction="col"
            className={cn(
              'h-full w-fit min-w-56 border-r border-neutral-200 transition-all transition-discrete duration-300',
              {
                hidden: !sideBarOpen,
              },
            )}
          >
            {isItemListLoading ? (
              <div className="p-5 overflow-auto w-full flex flex-col items-center max-h-full">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    className="min-w-full w-full px-2 py-1 flex flex-row items-center gap-2 border-b rounded-md border-neutral-100 animate-pulse"
                  >
                    <div className="flex flex-row items-center gap-3">
                      <div className="rounded overflow-hidden w-8 h-10 bg-neutral-200" />
                      <div className="w-20 h-4 bg-neutral-200 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-5 h-full w-full">
                <Virtuoso
                  data={reorderedItems || []}
                  overscan={200}
                  style={{ height: '100%', width: '100%' }}
                  endReached={() => {
                    if (hasNextPage && !isFetchingNextPage) {
                      fetchNextPage();
                    }
                  }}
                  itemContent={(index, item) => (
                    <SidebarListItem
                      key={item.id}
                      index={index}
                      item={item}
                      currentItemId={currentItemId}
                      userId={userId}
                      searchParams={location.search}
                    />
                  )}
                  components={{
                    Footer: () =>
                      isFetchingNextPage ? (
                        <div className="flex justify-center py-2">
                          <div className="w-5 h-5 border-2 border-neutral-300 border-t-primary-500 rounded-full animate-spin" />
                        </div>
                      ) : null,
                  }}
                />
              </div>
            )}
          </Stack>
          {children}
        </Stack>
      )}
    </Page.Container>
  );
}

export default ItemDetailsLayout;
